import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  ShieldAlert, Wrench, Download, Loader2, CheckCircle2, AlertTriangle, Search, RefreshCw,
  Trash2, Link2, GitMerge
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const jheaders = () => ({ 'Content-Type': 'application/json', ...headers() });

const ISSUE_LABELS = {
  no_user: { label: 'No User Account', cls: 'bg-red-100 text-red-700' },
  user_inactive: { label: 'User Inactive', cls: 'bg-orange-100 text-orange-700' },
  no_role: { label: 'No Role', cls: 'bg-amber-100 text-amber-700' },
  emp_inactive: { label: 'Employee Inactive', cls: 'bg-rose-100 text-rose-700' },
  no_entity: { label: 'No Entity', cls: 'bg-yellow-100 text-yellow-700' },
  not_linked: { label: 'Not Linked', cls: 'bg-fuchsia-100 text-fuchsia-700' },
};

export default function DataIntegrity() {
  const { user } = useAuth();
  const canAccess = ['hr', 'admin', 'super_admin'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [lookups, setLookups] = useState({ departments: [], categories: [], appointment_types: [] });
  const [filters, setFilters] = useState({ department: '', category: '', appointment_type: '', issue: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ department: '', category: '', appointment_type: '' });
  const [busy, setBusy] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [matchOrphan, setMatchOrphan] = useState(null); // {user, matches}
  const [matchLoading, setMatchLoading] = useState(false);

  const fetchOrphans = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/data-integrity/orphan-users`, { headers: headers(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setOrphans(data.users || []);
      }
    } catch {}
  }, []);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filters.department) q.set('department', filters.department);
      if (filters.category) q.set('category', filters.category);
      if (filters.appointment_type) q.set('appointment_type', filters.appointment_type);
      if (filters.issue) q.set('issue', filters.issue);
      const res = await fetch(`${API}/api/data-integrity/employees?${q}`, { headers: headers(), credentials: 'include' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Fetch failed');
      const data = await res.json();
      setEmployees(data.employees);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [filters]);

  const fetchLookups = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/data-integrity/lookups`, { headers: headers(), credentials: 'include' });
      if (res.ok) setLookups(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('hrms-token');
    if (canAccess && token) { fetchLookups(); fetchAudit(); fetchOrphans(); }
  }, [canAccess, fetchLookups, fetchAudit, fetchOrphans]);

  const promoteUser = async (user_id) => {
    if (!window.confirm('Create an Employee record for this user? They will then appear in the Employees tab and gain access to leave/attendance/payroll flows.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/users/${user_id}/promote-to-employee`, {
        method: 'POST', headers: jheaders(), credentials: 'include', body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Promote failed');
      toast.success(`Employee record created: ${data.employee_id}`);
      fetchAudit(); fetchOrphans();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const findMatches = async (u) => {
    setMatchLoading(true);
    setMatchOrphan({ user: u, matches: [] });
    try {
      const res = await fetch(`${API}/api/data-integrity/orphan-users/${u.user_id}/suggestions`, { headers: headers(), credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Lookup failed');
      setMatchOrphan({ user: data.user, matches: data.matches || [] });
    } catch (e) {
      toast.error(e.message);
      setMatchOrphan(null);
    } finally {
      setMatchLoading(false);
    }
  };

  const mergeInto = async (user_id, employee_id) => {
    if (!window.confirm(`Merge this orphan login into employee ${employee_id}? The orphan account will be DELETED — only the existing employee's login remains.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/orphan-users/${user_id}/merge-into/${employee_id}`, {
        method: 'POST', headers: jheaders(), credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Merge failed');
      toast.success('Merged. Orphan login removed.');
      setMatchOrphan(null);
      fetchOrphans();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const deleteOrphan = async (u) => {
    if (!window.confirm(`Permanently DELETE the login for ${u.email}? This cannot be undone.\n\nUse this when the same person uses another account with a different email.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/orphan-users/${u.user_id}`, {
        method: 'DELETE', headers: headers(), credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      toast.success(`Deleted ${u.email}`);
      fetchOrphans();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const changeOrphanDept = async (u) => {
    const newDept = window.prompt(`Set department for ${u.email}:`, u.department || '');
    if (!newDept || !newDept.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/orphan-users/${u.user_id}/change-department`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify({ department: newDept.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      toast.success(`Department updated to ${newDept.trim()}`);
      fetchOrphans();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const visible = useMemo(() => {
    if (!search.trim()) return employees;
    const s = search.toLowerCase();
    return employees.filter(e =>
      (e.first_name || '').toLowerCase().includes(s)
      || (e.last_name || '').toLowerCase().includes(s)
      || (e.email || '').toLowerCase().includes(s)
      || (e.employee_id || '').toLowerCase().includes(s)
    );
  }, [employees, search]);

  const stats = useMemo(() => {
    const issuesCount = employees.filter(e => e.audit?.has_issues).length;
    return { total: employees.length, withIssues: issuesCount, healthy: employees.length - issuesCount };
  }, [employees]);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAllVisible = () => {
    const visibleIds = visible.map(e => e.employee_id);
    if (visibleIds.every(id => selected.includes(id))) {
      setSelected(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelected(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const downloadExcel = async () => {
    try {
      const q = new URLSearchParams();
      if (filters.department) q.set('department', filters.department);
      if (filters.category) q.set('category', filters.category);
      if (filters.appointment_type) q.set('appointment_type', filters.appointment_type);
      if (filters.issue) q.set('issue', filters.issue);
      const res = await fetch(`${API}/api/data-integrity/employees-export?${q}`, { headers: headers(), credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data_integrity_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (e) { toast.error(e.message); }
  };

  const saveSingle = async () => {
    setBusy(true);
    try {
      const payload = {};
      ['department', 'category', 'appointment_type', 'designation', 'entity_id', 'is_active'].forEach(k => {
        if (editing[k] !== undefined) payload[k] = editing[k];
      });
      const res = await fetch(`${API}/api/data-integrity/employees/${editing.employee_id}/remap`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
      toast.success('Mapping updated');
      setEditing(null);
      fetchAudit();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const repair = async (employee_id) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/employees/${employee_id}/repair`, {
        method: 'POST', headers: jheaders(), credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Repair failed');
      if (data.actions?.length) {
        toast.success(data.message + (data.temp_password ? ` — temp password: ${data.temp_password}` : ''));
      } else {
        toast(data.message);
      }
      fetchAudit();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const bulkRemap = async (e) => {
    e?.preventDefault();
    if (selected.length === 0) { toast.error('Select at least one employee'); return; }
    const updates = {};
    if (bulkForm.department) updates.department = bulkForm.department;
    if (bulkForm.category) updates.category = bulkForm.category;
    if (bulkForm.appointment_type) updates.appointment_type = bulkForm.appointment_type;
    if (Object.keys(updates).length === 0) { toast.error('Choose at least one field'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/bulk-remap`, {
        method: 'PUT', headers: jheaders(), credentials: 'include',
        body: JSON.stringify({ employee_ids: selected, updates })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk remap failed');
      toast.success(`Updated ${data.modified} of ${selected.length} employees`);
      setBulkOpen(false);
      setBulkForm({ department: '', category: '', appointment_type: '' });
      setSelected([]);
      fetchAudit();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const bulkRepair = async () => {
    const ids = selected.length > 0 ? selected : null;
    if (!window.confirm(ids ? `Auto-repair ${ids.length} selected employees?` : 'Auto-repair ALL employees with issues? This will activate inactive records and create missing user accounts.')) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/data-integrity/bulk-repair`, {
        method: 'POST', headers: jheaders(), credentials: 'include',
        body: JSON.stringify(ids ? { employee_ids: ids } : {})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Bulk repair failed');
      const okCount = data.results.filter(r => r.ok).length;
      toast.success(`Repaired ${okCount}/${data.total} employees`);
      // Show any temp passwords created
      const newCreds = data.results.filter(r => r.temp_password);
      if (newCreds.length > 0) {
        console.table(newCreds.map(r => ({ employee_id: r.employee_id, temp_password: r.temp_password })));
        toast.info(`${newCreds.length} new user accounts created — check console for temp passwords`);
      }
      setSelected([]);
      fetchAudit();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  if (!canAccess) {
    return (
      <div className="p-6">
        <Card><CardContent className="py-10 text-center text-slate-500">
          <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Only HR / Super Admin can access the Data Integrity module.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="data-integrity-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-[#DC2626]" />
            Data Integrity & Mapping
          </h1>
          <p className="text-sm text-slate-500">Identify employees with broken access, missing user accounts, or wrong mappings — and fix them in one click.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchAudit} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
          <Button variant="outline" onClick={downloadExcel} data-testid="export-excel-btn"><Download className="w-4 h-4 mr-1.5" /> Export Excel</Button>
          <Button onClick={bulkRepair} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="auto-repair-all-btn">
            <Wrench className="w-4 h-4 mr-1.5" /> {selected.length > 0 ? `Repair ${selected.length} Selected` : 'Auto-Repair All Issues'}
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3 flex items-center justify-between">
          <div><p className="text-xs text-slate-500">Total Employees</p><p className="text-2xl font-bold">{stats.total}</p></div>
        </CardContent></Card>
        <Card className="border-emerald-200"><CardContent className="p-3 flex items-center justify-between">
          <div><p className="text-xs text-emerald-700">Healthy</p><p className="text-2xl font-bold text-emerald-700">{stats.healthy}</p></div>
          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
        </CardContent></Card>
        <Card className={stats.withIssues > 0 ? 'border-red-300' : 'border-slate-200'}><CardContent className="p-3 flex items-center justify-between">
          <div><p className="text-xs text-red-700">With Issues</p><p className={`text-2xl font-bold ${stats.withIssues > 0 ? 'text-red-700' : 'text-slate-400'}`}>{stats.withIssues}</p></div>
          <AlertTriangle className={`w-6 h-6 ${stats.withIssues > 0 ? 'text-red-500' : 'text-slate-300'}`} />
        </CardContent></Card>
      </div>

      {/* Users without Employee records */}
      {orphans.length > 0 && (
        <Card className="border-orange-300" data-testid="orphan-users-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              {orphans.length} user{orphans.length > 1 ? 's' : ''} have a login account but NO employee record
            </CardTitle>
            <p className="text-xs text-slate-500">These users were created via Admin Panel → Users without going through the Employees form. They cannot access leave / attendance / payroll until promoted.</p>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Department</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {orphans.map(u => (
                  <TableRow key={u.user_id} data-testid={`orphan-row-${u.user_id}`}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{u.role}</Badge></TableCell>
                    <TableCell>{u.department || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-blue-700 border-blue-300" onClick={() => findMatches(u)} disabled={busy} data-testid={`match-${u.user_id}`} title="Find existing employee that matches">
                          <Search className="w-3.5 h-3.5 mr-1" /> Find Match
                        </Button>
                        <Button size="sm" className="h-7 px-2 bg-orange-600 hover:bg-orange-700 text-white" onClick={() => promoteUser(u.user_id)} disabled={busy} data-testid={`promote-${u.user_id}`} title="Create a new Employee record">
                          <Wrench className="w-3.5 h-3.5 mr-1" /> Promote
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => changeOrphanDept(u)} disabled={busy} data-testid={`chdept-${u.user_id}`} title="Change department">
                          <Link2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-red-600 border-red-300" onClick={() => deleteOrphan(u)} disabled={busy} data-testid={`del-${u.user_id}`} title="Delete this orphan login">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card><CardContent className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <Input placeholder="Search name / email / ID" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="search-input" />
          </div>
          <Select value={filters.department || 'all'} onValueChange={(v) => setFilters({ ...filters, department: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {lookups.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category || 'all'} onValueChange={(v) => setFilters({ ...filters, category: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Staff Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff types</SelectItem>
              {lookups.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.appointment_type || 'all'} onValueChange={(v) => setFilters({ ...filters, appointment_type: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="filter-appt"><SelectValue placeholder="Appointment Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All appointments</SelectItem>
              {lookups.appointment_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.issue || 'all'} onValueChange={(v) => setFilters({ ...filters, issue: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="filter-issue"><SelectValue placeholder="Issue Filter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Show all</SelectItem>
              <SelectItem value="any">Any issue</SelectItem>
              <SelectItem value="no_user">Missing user account</SelectItem>
              <SelectItem value="user_inactive">User inactive</SelectItem>
              <SelectItem value="emp_inactive">Employee inactive</SelectItem>
              <SelectItem value="not_linked">Employee↔User not linked</SelectItem>
              <SelectItem value="no_entity">Missing entity</SelectItem>
              <SelectItem value="no_role">No role</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      {/* Bulk action toolbar */}
      {selected.length > 0 && (
        <div className="p-3 rounded border-2 border-[#DC2626] bg-red-50 flex flex-wrap gap-2 items-center" data-testid="bulk-toolbar">
          <span className="text-sm font-semibold text-[#DC2626]">{selected.length} selected</span>
          <Button size="sm" onClick={() => setBulkOpen(true)} className="bg-[#DC2626] text-white hover:bg-[#B91C1C]" data-testid="bulk-remap-btn">Bulk Remap</Button>
          <Button size="sm" onClick={bulkRepair} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="bulk-repair-btn"><Wrench className="w-3.5 h-3.5 mr-1" />Auto-Repair Selected</Button>
          <Button size="sm" variant="outline" onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</Button>
        </div>
      )}

      {/* Table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="w-8 h-8 text-[#DC2626] animate-spin mx-auto" /></div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-slate-400">No employees match the filters.</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10">
                <input type="checkbox"
                  checked={visible.length > 0 && visible.every(e => selected.includes(e.employee_id))}
                  onChange={toggleSelectAllVisible}
                  data-testid="select-all"
                  className="w-4 h-4 accent-[#DC2626]" />
              </TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Staff Type</TableHead>
              <TableHead>Appointment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Issues</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {visible.map(e => {
                const a = e.audit || {};
                return (
                  <TableRow key={e.employee_id} data-testid={`emp-row-${e.employee_id}`} className={a.has_issues ? 'bg-red-50/40' : ''}>
                    <TableCell><input type="checkbox" checked={selected.includes(e.employee_id)} onChange={() => toggleSelect(e.employee_id)} data-testid={`select-${e.employee_id}`} className="w-4 h-4 accent-[#DC2626]" /></TableCell>
                    <TableCell><p className="font-medium">{e.first_name} {e.last_name}</p><p className="text-[11px] text-slate-500">{e.email || 'no email'}</p><p className="text-[10px] text-slate-400">{e.employee_id}</p></TableCell>
                    <TableCell>{e.department || <span className="text-amber-600 text-xs">Not set</span>}</TableCell>
                    <TableCell>{e.category || <span className="text-amber-600 text-xs">Not set</span>}</TableCell>
                    <TableCell>{e.appointment_type || '—'}</TableCell>
                    <TableCell>
                      {e.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge className="bg-rose-100 text-rose-700">Inactive</Badge>}
                      {a.has_user_account ? (
                        a.user_is_active ? <Badge className="ml-1 bg-blue-100 text-blue-700">User: {a.user_role || 'no role'}</Badge>
                          : <Badge className="ml-1 bg-orange-100 text-orange-700">User off</Badge>
                      ) : <Badge className="ml-1 bg-red-100 text-red-700">No user</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(a.issues || []).map(i => (<Badge key={i} className={`text-[10px] ${ISSUE_LABELS[i]?.cls || 'bg-slate-100'}`}>{ISSUE_LABELS[i]?.label || i}</Badge>))}
                        {(!a.issues || a.issues.length === 0) && <span className="text-xs text-emerald-600">All good</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setEditing({ ...e, ...e })} data-testid={`edit-${e.employee_id}`}>Edit</Button>
                      {a.has_issues && (
                        <Button size="sm" className="ml-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => repair(e.employee_id)} data-testid={`repair-${e.employee_id}`}>
                          <Wrench className="w-3 h-3 mr-1" /> Repair
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      {/* Single edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Mapping — {editing?.first_name} {editing?.last_name}</DialogTitle>
            <DialogDescription>{editing?.email} · {editing?.employee_id}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 mt-2">
              <div><Label>Department</Label><Input value={editing.department || ''} onChange={(e) => setEditing({ ...editing, department: e.target.value })} data-testid="edit-department" /></div>
              <div><Label>Staff Type (Category)</Label><Input value={editing.category || ''} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="edit-category" /></div>
              <div>
                <Label>Appointment Type</Label>
                <Select value={editing.appointment_type || ''} onValueChange={(v) => setEditing({ ...editing, appointment_type: v })}>
                  <SelectTrigger data-testid="edit-appt"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['Permanent', 'Contract', 'Adhoc', 'Guest', 'Regular'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Designation</Label><Input value={editing.designation || ''} onChange={(e) => setEditing({ ...editing, designation: e.target.value })} data-testid="edit-designation" /></div>
              <div><Label>Entity ID</Label><Input value={editing.entity_id || ''} onChange={(e) => setEditing({ ...editing, entity_id: e.target.value })} data-testid="edit-entity" /></div>
              <div className="flex items-center gap-2">
                <input id="is_active" type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="w-4 h-4 accent-[#DC2626]" data-testid="edit-is-active" />
                <Label htmlFor="is_active">Employee is Active</Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" onClick={saveSingle} disabled={busy} data-testid="save-edit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk remap dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Remap — {selected.length} employees</DialogTitle>
            <DialogDescription>Empty fields will be skipped. Linked user records' department/entity are synced automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={bulkRemap} className="space-y-3 mt-2">
            <div><Label>Department</Label><Input value={bulkForm.department} onChange={(e) => setBulkForm({ ...bulkForm, department: e.target.value })} placeholder="Leave empty to skip" data-testid="bulk-department" /></div>
            <div><Label>Staff Type (Category)</Label><Input value={bulkForm.category} onChange={(e) => setBulkForm({ ...bulkForm, category: e.target.value })} placeholder="Teaching / non_teaching / ..." data-testid="bulk-category" /></div>
            <div>
              <Label>Appointment Type</Label>
              <Select value={bulkForm.appointment_type || 'unset'} onValueChange={(v) => setBulkForm({ ...bulkForm, appointment_type: v === 'unset' ? '' : v })}>
                <SelectTrigger data-testid="bulk-appt"><SelectValue placeholder="Skip" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">— Skip —</SelectItem>
                  {['Permanent', 'Contract', 'Adhoc', 'Guest', 'Regular'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" disabled={busy} data-testid="bulk-apply-btn">Apply to Selected</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Match-suggestion dialog: pick an existing employee to merge an orphan into */}
      <Dialog open={!!matchOrphan} onOpenChange={(o) => { if (!o) setMatchOrphan(null); }}>
        <DialogContent className="max-w-3xl" data-testid="orphan-match-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5 text-blue-600" /> Find existing employee for this login</DialogTitle>
            <DialogDescription>
              Orphan login: <strong>{matchOrphan?.user?.name}</strong> ({matchOrphan?.user?.email}). Pick the existing employee record this person actually is — we will merge the login and delete the orphan account.
            </DialogDescription>
          </DialogHeader>
          {matchLoading ? (
            <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="w-5 h-5 mr-2 animate-spin" />Searching for matches…</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {(matchOrphan?.matches || []).length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">
                  No similar employee record found.
                  <div className="mt-3">
                    Use <strong>Promote</strong> to create a fresh employee record, <strong>Change Department</strong> to update the dept, or <strong>Delete</strong> if this login is a duplicate.
                  </div>
                </div>
              ) : (
                matchOrphan.matches.map((m) => (
                  <div key={m.employee_id} className="flex items-center justify-between gap-3 border rounded-md p-3 hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`match-row-${m.employee_id}`}>
                    <div className="flex-1">
                      <div className="font-medium">{m.first_name} {m.last_name} <span className="text-xs text-slate-400 ml-2">({m.employee_id})</span></div>
                      <div className="text-xs text-slate-500">{m.email} · {m.phone || '—'}</div>
                      <div className="text-xs text-slate-500">{m.designation || '—'} · {m.department || '—'}</div>
                    </div>
                    <Badge className={m.match_score >= 100 ? 'bg-green-100 text-green-700' : m.match_score >= 60 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                      score {m.match_score}
                    </Badge>
                    <Button size="sm" onClick={() => mergeInto(matchOrphan.user.user_id, m.employee_id)} disabled={busy} data-testid={`merge-into-${m.employee_id}`} className="bg-blue-600 hover:bg-blue-700 text-white">
                      <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge here
                    </Button>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
